import { Grid, TextField } from '@mui/material';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';

import { useEffect, useRef, useState } from 'react';

import { toSentenceCase } from 'common/utils/utilFunctions';

import { sliderChangeValue } from './RangeSliderUtils';

import styles from './RangeSlider.module.scss';

interface IRangeSlider {
	minRange: string;
	maxRange: string;
	title: string;
	steps?: number;
	onRangeValueChange: (value: number) => void;
	readonly?: boolean;
	defaultValue?: number | string;
	disabled?: boolean;
}

const RangeSlider = (props: IRangeSlider) => {
	const {
		defaultValue,
		minRange,
		maxRange,
		steps,
		title,
		onRangeValueChange,
		readonly,
		disabled = false,
	} = props;
	const { sliderHeight, highlightSlider } = styles;

	const sliderStyle = {
		height: sliderHeight,
		'& .MuiSlider-track': {
			height: highlightSlider,
			'&:hover, &:active': {
				boxShadow: 'none',
			},
		},
		'& .MuiSlider-thumb': {
			'&:hover, &:active': {
				boxShadow: 'none',
			},
		},
	};

	const inputStyle = {
		'& .MuiInputBase-input.MuiOutlinedInput-input': {
			padding: 0,
		},
	};

	/* Local states */
	const [val, setVal] = useState<string | number>(
		Number(defaultValue) || minRange,
	);
	const inputRef = useRef<HTMLInputElement>(null);

	/**
	 * @description method to handle slider range change
	 */
	const handleSliderChange = (_: Event, newValue: number | number[]) => {
		const updatedValue = sliderChangeValue(newValue, minRange, maxRange);
		setVal(updatedValue);
	};

	/**
	 * @description method to handle slider input field change
	 */
	const handleSliderInputChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const inputValue = event.target.value;
		const filteredValue = inputValue.replace(/[^0-9.]/g, '');
		setVal(filteredValue);
	};

	/**
	 * @description method to handle blur of text field
	 */
	const handleBlur = () => {
		if (Number(val) < Number(minRange)) {
			setVal(minRange);
		} else if (Number(val) > Number(maxRange)) {
			setVal(maxRange);
		}
	};

	/**
	 * @description method to handle focus of input field
	 */
	const onInputFocus = () => {
		inputRef?.current?.select();
	};

	/* calls the function on change of val */
	useEffect(() => {
		onRangeValueChange(Number(val));
	}, [val]);

	return (
		<div className={styles['display-container']}>
			<Typography data-testid="slider-title">
				{toSentenceCase(title)}
			</Typography>
			<Grid container spacing={2}>
				<Grid
					item
					xs={9}
					lg={9}
					md={9}
					sm={9}
					className={styles['range-silder-container']}
				>
					<Slider
						step={steps}
						value={Number(val)}
						min={Number(minRange)}
						max={Number(maxRange)}
						onChange={handleSliderChange}
						sx={sliderStyle}
						disabled={disabled}
					/>
				</Grid>
				<Grid
					item
					xs={3}
					lg={3}
					md={3}
					sm={3}
					className={styles['count-container']}
				>
					<TextField
						value={!val ? Number(minRange)?.toFixed(1) : val}
						size="small"
						className={styles['display-count']}
						inputRef={inputRef}
						onChange={handleSliderInputChange}
						onBlur={handleBlur}
						onFocus={onInputFocus}
						disabled={readonly}
						type="text"
						inputProps={{
							step: steps,
							min: Number(minRange),
							max: Number(maxRange),
							'aria-labelledby': 'input-slider',
							'data-testid': 'count-text-field',
						}}
						sx={inputStyle}
					/>
				</Grid>
			</Grid>
		</div>
	);
};

export default RangeSlider;
